import { NAV } from './nav.js';
import { auth, ready, shopName, sync, version } from './lib/khata.js';
import { t } from './lib/i18n.js';
import { go, href, route, type Route } from './lib/router.js';
import { sheet, toast, wide } from './lib/ui.js';
import { ErrorBoundary } from './components/ErrorBoundary.js';
import { Bank } from './screens/Bank.js';
import { Login } from './screens/Login.js';
import { Memo } from './screens/Memo.js';
import { Books } from './screens/Books.js';
import { AccountLedger } from './screens/AccountLedger.js';
import { Money } from './screens/Money.js';
import { More } from './screens/More.js';
import { Parties } from './screens/Parties.js';
import { Party } from './screens/Party.js';
import { Payments } from './screens/Payments.js';
import { Reports } from './screens/Reports.js';
import { Returns } from './screens/Returns.js';
import { Settings } from './screens/Settings.js';
import { Stock } from './screens/Stock.js';
import { Today } from './screens/Today.js';
import { Tx } from './screens/Tx.js';
import { SheetHost } from './sheets/index.js';

function Screen(props: { route: Route }) {
  const r = props.route;
  switch (r.name) {
    case 'today':
      return <Today />;
    case 'stock':
      return <Stock filter={r.filter} />;
    case 'sales':
      return <Tx kind="sale" />;
    case 'purchases':
      return <Tx kind="purchase" />;
    case 'parties':
      return <Parties />;
    case 'party':
      return <Party id={r.id} />;
    case 'payments':
      return <Payments />;
    case 'returns':
      return <Returns />;
    case 'money':
      return <Money />;
    case 'bank':
      return <Bank />;
    case 'reports':
      return <Reports />;
    case 'more':
      return <More />;
    case 'settings':
      return <Settings />;
    case 'memo':
      return <Memo id={r.id} />;
    case 'books':
      return <Books />;
    case 'ledger':
      return <AccountLedger id={r.id} />;
  }
}

function isCurrent(r: Route, name: string): boolean {
  return (
    r.name === name ||
    (name === 'parties' && r.name === 'party') ||
    (name === 'bank' && r.name === 'ledger') ||
    (name === 'more' && (r.name === 'settings' || r.name === 'books'))
  );
}

export function App() {
  if (!ready.value) {
    return (
      <div class="login">
        <div class="muted">{t('opening')}</div>
      </div>
    );
  }
  if (auth.value === 'login' || auth.value === 'setup' || auth.value === 'unknown')
    return <Login />;
  const r = route.value;
  const isWide = wide.value;
  const name = shopName.value || t('appName');
  const brand = name.split(' ');
  const last = brand.length > 1 ? brand.pop() : '';
  const bottom = [
    ...NAV.filter((n) => n.bottom),
    { name: 'more' as const, label: 'nav_more' as const, bottom: true },
  ];
  const syncLabel =
    sync.value === 'offline'
      ? t('sync_offline')
      : sync.value === 'syncing'
        ? t('sync_syncing')
        : sync.value === 'error'
          ? t('sync_error')
          : t('sync_synced');

  return (
    <div class={'shell' + (isWide ? ' wide' : '')}>
      {isWide && (
        <nav class="nav">
          <div class="nav-brand">
            {brand.join(' ')}
            {last && (
              <>
                <br />
                <em>{last}</em>
              </>
            )}
          </div>
          {NAV.map((n) => (
            <a
              key={n.name}
              class="nav-item"
              href={href(n.name)}
              aria-current={isCurrent(r, n.name) ? 'page' : undefined}
              onClick={(e) => (e.preventDefault(), go(n.name))}
            >
              <span class="dot" />
              {t(n.label)}
            </a>
          ))}
          <div class="nav-spacer" />
          <a
            class="nav-item"
            href={href('settings')}
            aria-current={r.name === 'settings' ? 'page' : undefined}
            onClick={(e) => (e.preventDefault(), go('settings'))}
          >
            <span class="dot" />
            {t('nav_settings')}
          </a>
          <div class="nav-foot">
            {syncLabel}
            {version.value ? ' · ' + version.value : ''}
          </div>
        </nav>
      )}
      <main class={'main' + (isWide ? '' : ' narrow')}>
        {sync.value === 'offline' && <div class="banner">{t('banner_offline')}</div>}
        {sync.value === 'error' && <div class="banner">{t('banner_syncError')}</div>}
        <ErrorBoundary key={JSON.stringify(r)}>
          <Screen route={r} />
        </ErrorBoundary>
      </main>
      {!isWide && (
        <nav class="tabbar">
          {bottom.map((n) => (
            <a
              key={n.name}
              class="tab"
              href={href(n.name)}
              aria-current={isCurrent(r, n.name) ? 'page' : undefined}
              onClick={(e) => (e.preventDefault(), go(n.name))}
            >
              <span class="dot" />
              {t(n.label)}
            </a>
          ))}
        </nav>
      )}
      {sheet.value && (
        <ErrorBoundary>
          <SheetHost />
        </ErrorBoundary>
      )}
      {toast.value && <div class={'toast' + (isWide ? '' : ' narrow-toast')}>{toast.value}</div>}
    </div>
  );
}
